import { AlertTriangle, BarChart3, Download, Share2, Table, Users } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import EmptyStateGuide from '../../components/analytics/EmptyStateGuide';
import MetricTooltip from '../../components/analytics/MetricTooltip';
import PageGuide from '../../components/analytics/PageGuide';
import { analyticsService, tenantService, venueService } from '../../services/api';
import '../Analytics.css';
import AnalyticsLayout from './AnalyticsLayout';

// ── Formatters ────────────────────────────────────────────────────────────────

function getDefaultFromDate() {
    const d = new Date();
    d.setDate(d.getDate() - 90);
    return d.toISOString().split('T')[0];
}

const formatCurrency = (v) =>
    new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(v || 0);

const formatNumber = (v) => new Intl.NumberFormat('it-IT').format(v || 0);

const formatPct = (v) => (v != null ? v.toFixed(1) + '%' : '—');

/**
 * Format a period ISO string to a human-readable label based on groupBy.
 * Pass partial=true to append a "(parziale)" suffix.
 */
function formatPeriodLabel(isoStr, groupBy, partial) {
    if (!isoStr) return '—';
    const d = new Date(isoStr);
    let label;
    switch (groupBy) {
        case 'day':
            label = d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
            break;
        case 'month':
            label = d.toLocaleDateString('it-IT', { month: 'short', year: 'numeric' });
            break;
        case 'quarter': {
            const q = Math.floor(d.getMonth() / 3) + 1;
            label = `Q${q} ${d.getFullYear()}`;
            break;
        }
        case 'semester': {
            const s = d.getMonth() < 6 ? 'H1' : 'H2';
            label = `${s} ${d.getFullYear()}`;
            break;
        }
        case 'year':
            label = String(d.getFullYear());
            break;
        default:
            label = d.toLocaleDateString('it-IT');
    }
    return partial ? `${label} (parziale)` : label;
}

const GROUP_BY_OPTIONS = [
    { value: 'day', label: 'Giorno' },
    { value: 'month', label: 'Mese' },
    { value: 'quarter', label: 'Trimestre' },
    { value: 'semester', label: 'Semestre' },
    { value: 'year', label: 'Anno' },
];

/**
 * Smart groupBy selection based on period length in days.
 * 7d → day, 30d → month, 90d → month, 1y → quarter, All → year
 */
function getSmartGroupBy(fromStr, toStr) {
    if (!fromStr || !toStr) return 'month';
    const days = Math.round((new Date(toStr) - new Date(fromStr)) / (1000 * 60 * 60 * 24));
    if (days <= 7) return 'day';
    if (days <= 90) return 'month';
    if (days <= 365) return 'quarter';
    return 'year';
}

/**
 * Check if a period is "partial" (the selected date range doesn't cover
 * the full unit — e.g. the filter starts mid-month).
 */
function isPeriodPartial(periodKey, groupBy, fromStr, toStr) {
    if (!periodKey || groupBy === 'day') return false;
    const periodStart = new Date(periodKey);
    let periodEnd;
    switch (groupBy) {
        case 'month':
            periodEnd = new Date(periodStart.getFullYear(), periodStart.getMonth() + 1, 0);
            break;
        case 'quarter':
            periodEnd = new Date(periodStart.getFullYear(), periodStart.getMonth() + 3, 0);
            break;
        case 'semester':
            periodEnd = new Date(periodStart.getFullYear(), periodStart.getMonth() + 6, 0);
            break;
        case 'year':
            periodEnd = new Date(periodStart.getFullYear(), 11, 31);
            break;
        default:
            return false;
    }
    const rangeFrom = new Date(fromStr);
    const rangeTo = new Date(toStr);
    return rangeFrom > periodStart || rangeTo < periodEnd;
}

// ── Sub-components ────────────────────────────────────────────────────────────

function StripeBadge({ canReceive }) {
    return canReceive ? (
        <span className="td-stripe-badge td-stripe-badge--ok">Stripe OK</span>
    ) : (
        <span className="td-stripe-badge td-stripe-badge--warn">⚠ Non eleggibile</span>
    );
}

function ReconStatusBadge({ status }) {
    return status === 'ok' ? (
        <span className="td-recon-badge td-recon-badge--ok">✓ OK</span>
    ) : status === 'partial' ? (
        <span className="td-recon-badge td-recon-badge--check">Copertura parziale</span>
    ) : (
        <span className="td-recon-badge td-recon-badge--check">Da verificare</span>
    );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function AnalyticsTipDistribution() {
    const [layoutFilters, setLayoutFilters] = useState({
        from: getDefaultFromDate(),
        to: new Date().toISOString().split('T')[0],
    });

    const [localFilters, setLocalFilters] = useState({
        tenantId: '',
        venueId: '',
        groupBy: 'month',
    });

    // Whether the user has manually set groupBy (prevents auto-override on date change)
    const [groupByManual, setGroupByManual] = useState(false);

    const [workerFilter, setWorkerFilter] = useState(''); // client-side only

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [tenants, setTenants] = useState([]);
    const [allVenues, setAllVenues] = useState([]);
    const [metaLoading, setMetaLoading] = useState(true);

    const [viewMode, setViewMode] = useState('table'); // 'table' | 'matrix'

    // ── Load tenants + venues once ──────────────────────────────────────────
    useEffect(() => {
        async function loadMeta() {
            try {
                const [tenantsRes, venuesRes] = await Promise.all([
                    tenantService.getTenants(),
                    venueService.getVenues(),
                ]);
                setTenants(tenantsRes.data || tenantsRes || []);
                setAllVenues(venuesRes.venues || venuesRes.data || []);
            } catch (err) {
                console.error('Failed to load tenants/venues:', err);
            } finally {
                setMetaLoading(false);
            }
        }
        loadMeta();
    }, []);

    // ── Smart groupBy: auto-update when date range changes (unless manually set) ──
    useEffect(() => {
        if (!groupByManual) {
            const smart = getSmartGroupBy(layoutFilters.from, layoutFilters.to);
            setLocalFilters((prev) => ({ ...prev, groupBy: smart }));
        }
    }, [layoutFilters.from, layoutFilters.to, groupByManual]);

    // ── Derived: filtered venue list by selected tenant ─────────────────────
    const filteredVenues = localFilters.tenantId
        ? allVenues.filter((v) => v.tenantId === localFilters.tenantId)
        : allVenues;

    // ── Load distribution data when relevant filters change ─────────────────
    const loadData = useCallback(async () => {
        if (!localFilters.venueId && !localFilters.tenantId) {
            setData(null);
            return;
        }
        try {
            setLoading(true);
            setError(null);
            const result = await analyticsService.getTipDistribution({
                venueId: localFilters.venueId || undefined,
                tenantId: localFilters.tenantId || undefined,
                from: layoutFilters.from,
                to: layoutFilters.to,
                groupBy: localFilters.groupBy,
            });
            setData(result);
            setWorkerFilter(''); // reset worker filter on new data load
        } catch (err) {
            console.error('Failed to load tip distribution:', err);
            setError(err.response?.data?.message || err.message || 'Errore nel caricamento');
        } finally {
            setLoading(false);
        }
    }, [localFilters.venueId, localFilters.tenantId, localFilters.groupBy, layoutFilters.from, layoutFilters.to]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // ── Handlers ────────────────────────────────────────────────────────────
    function handleTenantChange(tenantId) {
        setLocalFilters((prev) => ({
            ...prev,
            tenantId,
            venueId: '', // reset venue when tenant changes
        }));
        setGroupByManual(false);
        setWorkerFilter('');
        setData(null);
    }

    function handleVenueChange(venueId) {
        setLocalFilters((prev) => ({ ...prev, venueId }));
        setGroupByManual(false);
        setWorkerFilter('');
        setData(null);
    }

    function handleGroupByChange(groupBy) {
        setLocalFilters((prev) => ({ ...prev, groupBy }));
        setGroupByManual(true);
    }

    // ── Derived: rows filtered by selected worker (client-side) ─────────────
    const visibleRows = workerFilter
        ? (data?.rows || []).filter((r) => r.destination === workerFilter)
        : (data?.rows || []);

    // Distinct workers from all rows (for the worker dropdown)
    const availableWorkers = data
        ? [...new Map(data.rows.map((r) => [r.destination, r.workerName])).entries()].map(
            ([dest, name]) => ({ destination: dest, workerName: name })
        )
        : [];

    // ── Selected venue name for display ────────────────────────────────────
    const selectedVenueName =
        allVenues.find((v) => v.id === localFilters.venueId)?.name || null;

    // ── Top worker tie / uniform distribution logic ──────────────────────────
    const topWorkerInfo = (() => {
        if (!data || !data.matrix?.workers?.length) return null;
        const sorted = [...data.matrix.workers].sort((a, b) => {
            const tA = Object.values(a.periods).reduce((s, p) => s + p.amount, 0);
            const tB = Object.values(b.periods).reduce((s, p) => s + p.amount, 0);
            return tB - tA;
        });
        const totals = sorted.map((w) => ({
            ...w,
            total: Object.values(w.periods).reduce((s, p) => s + p.amount, 0),
        }));
        if (totals.length === 0) return null;
        const maxAmount = totals[0].total;
        const topWorkers = totals.filter((w) => Math.abs(w.total - maxAmount) < 0.01);
        const isUniform = topWorkers.length === totals.length && totals.length > 1;
        return { topWorkers, maxAmount, isUniform, isTie: topWorkers.length > 1 && !isUniform };
    })();

    // ── Partial period detection ─────────────────────────────────────────────
    const partialPeriods = (() => {
        if (!data?.matrix?.periods) return new Set();
        const set = new Set();
        data.matrix.periods.forEach((pk) => {
            if (isPeriodPartial(pk, data.period.groupBy, data.period.from, data.period.to)) {
                set.add(pk);
            }
        });
        return set;
    })();
    const hasPartialPeriods = partialPeriods.size > 0;

    // ── Flat table totals ────────────────────────────────────────────────────
    const tableTotalAmount = visibleRows.reduce((s, r) => s + r.distributedAmount, 0);
    const tableTotalTx = visibleRows.reduce((s, r) => s + r.transactionCount, 0);
    const tableTotalAvgPerTx = tableTotalTx > 0 ? tableTotalAmount / tableTotalTx : 0;

    // ── CSV Export ───────────────────────────────────────────────────────────
    function handleExportCsv() {
        if (!data || visibleRows.length === 0) return;
        const venueName = allVenues.find((v) => v.id === localFilters.venueId)?.name || '—';
        const tenant = tenants.find((t) => t.id === localFilters.tenantId);
        const tenantName = tenant?.adminAlias || tenant?.name || '—';

        const headers = [
            'Tenant', 'Locale', 'Da', 'A', 'Raggruppa per',
            'Periodo', 'Percettore', 'Importo distribuito (EUR)',
            '% totale', 'Transazioni', 'Media/tx (EUR)', 'Stripe Connect',
        ];
        const rows = visibleRows.map((r) => {
            const partial = partialPeriods.has(r.periodKey);
            return [
                tenantName,
                venueName,
                data.period.from,
                data.period.to,
                localFilters.groupBy,
                formatPeriodLabel(r.periodKey, data.period.groupBy, partial),
                r.workerName,
                r.distributedAmount.toFixed(2),
                r.sharePercent != null ? r.sharePercent.toFixed(1) : '',
                r.transactionCount,
                r.averagePerTransaction.toFixed(2),
                r.canReceivePayments ? 'OK' : 'Non eleggibile',
            ];
        });
        const csv = [headers, ...rows]
            .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
            .join('\n');
        const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `distribuzione-mance-${localFilters.venueId || 'tenant'}-${data.period.from}-${data.period.to}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    }

    // ── Render ──────────────────────────────────────────────────────────────
    return (
        <AnalyticsLayout filters={layoutFilters} onFiltersChange={setLayoutFilters}>
            <PageGuide
                title="Distribuzione Mance"
                summary="Quote di mancia assegnate ai percettori tramite TipTransfer. Seleziona un locale e raggruppa per periodo per visualizzare la ripartizione. I dati non indicano che il payout bancario sia già stato liquidato."
            />

            {/* ── Filter Bar ────────────────────────────────────────────── */}
            <div className="td-filter-bar">
                <div className="td-filter-group">
                    <label className="td-filter-label">Tenant</label>
                    <select
                        className="td-filter-select"
                        value={localFilters.tenantId}
                        onChange={(e) => handleTenantChange(e.target.value)}
                        disabled={metaLoading}
                    >
                        <option value="">Tutti i tenant</option>
                        {tenants.map((t) => (
                            <option key={t.id} value={t.id}>
                                {t.adminAlias || t.name || t.company_name || t.id}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="td-filter-group">
                    <label className="td-filter-label">Locale *</label>
                    <select
                        className="td-filter-select"
                        value={localFilters.venueId}
                        onChange={(e) => handleVenueChange(e.target.value)}
                        disabled={metaLoading}
                    >
                        <option value="">Seleziona un locale…</option>
                        {filteredVenues.map((v) => (
                            <option key={v.id} value={v.id}>
                                {v.name}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="td-filter-group">
                    <label className="td-filter-label">
                        Raggruppa per{groupByManual ? '' : ' (auto)'}
                    </label>
                    <select
                        className="td-filter-select"
                        value={localFilters.groupBy}
                        onChange={(e) => handleGroupByChange(e.target.value)}
                    >
                        {GROUP_BY_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>
                                {o.label}
                            </option>
                        ))}
                    </select>
                </div>

                {availableWorkers.length > 0 && (
                    <div className="td-filter-group">
                        <label className="td-filter-label">Percettore</label>
                        <select
                            className="td-filter-select"
                            value={workerFilter}
                            onChange={(e) => setWorkerFilter(e.target.value)}
                        >
                            <option value="">Tutti i percettori</option>
                            {availableWorkers.map((w) => (
                                <option key={w.destination} value={w.destination}>
                                    {w.workerName}
                                </option>
                            ))}
                        </select>
                    </div>
                )}
            </div>

            {/* ── Loading ──────────────────────────────────────────────── */}
            {loading && (
                <div className="analytics-loading">
                    <div className="spinner"></div>
                    <p>Caricamento distribuzione mance…</p>
                </div>
            )}

            {/* ── Error ────────────────────────────────────────────────── */}
            {!loading && error && (
                <div className="alert alert-error" style={{ marginBottom: '20px' }}>
                    <strong>Errore:</strong> {error}
                </div>
            )}

            {/* ── Empty state: no venue selected ───────────────────────── */}
            {!loading && !error && !localFilters.venueId && !localFilters.tenantId && (
                <EmptyStateGuide
                    icon={Users}
                    title="Seleziona un locale"
                    message="Scegli un locale dal filtro in alto per visualizzare la distribuzione delle mance per percettore nel periodo selezionato."
                />
            )}

            {/* ── Empty state: venue selected but no distribution rows ─── */}
            {!loading && !error && (localFilters.venueId || localFilters.tenantId) && data && data.rows.length === 0 && (
                <EmptyStateGuide
                    icon={Share2}
                    title="Nessuna distribuzione nel periodo"
                    message={
                        data.successfulTransactions > 0
                            ? `Il locale ha ${formatNumber(data.successfulTransactions)} transazioni riuscite, ma non risultano TipTransfer nel periodo. Verifica la logica di split.`
                            : 'Nessuna transazione riuscita con TipTransfer trovata per questo locale nel periodo selezionato.'
                    }
                />
            )}

            {/* ── Data: KPIs + Reconciliation + Table/Matrix ───────────── */}
            {!loading && !error && data && data.rows.length > 0 && (
                <>
                    {/* ── KPI Cards ─────────────────────────────────────── */}
                    <div className="kpi-grid" style={{ marginBottom: '24px' }}>

                        <MetricTooltip metricKey="tipDistributedTotal">
                            <div className="kpi-card kpi-accent">
                                <div className="kpi-icon"><Share2 size={28} /></div>
                                <div className="kpi-content">
                                    <div className="kpi-label">Totale distribuito</div>
                                    <div className="kpi-value">
                                        {formatCurrency(
                                            workerFilter
                                                ? tableTotalAmount
                                                : data.totals.distributedAmount
                                        )}
                                    </div>
                                    <div className="kpi-meta">
                                        {formatNumber(
                                            workerFilter
                                                ? tableTotalTx
                                                : data.totals.transactionCount
                                        )} transazioni
                                    </div>
                                </div>
                            </div>
                        </MetricTooltip>

                        <MetricTooltip metricKey="tipAvgPerWorker">
                            <div className="kpi-card kpi-info">
                                <div className="kpi-icon"><BarChart3 size={28} /></div>
                                <div className="kpi-content">
                                    <div className="kpi-label">Media per percettore</div>
                                    <div className="kpi-value">
                                        {formatCurrency(
                                            workerFilter
                                                ? tableTotalAmount
                                                : data.totals.averagePerWorker
                                        )}
                                    </div>
                                    <div className="kpi-meta">
                                        {workerFilter ? '1' : formatNumber(data.totals.workerCount)}{' '}
                                        percettore{!workerFilter && data.totals.workerCount !== 1 ? 'i' : ''}
                                        {selectedVenueName ? ` · ${selectedVenueName}` : ''}
                                    </div>
                                </div>
                            </div>
                        </MetricTooltip>

                        {!workerFilter && data.totals.spreadMaxMin != null && (
                            <MetricTooltip metricKey="tipSpreadDistribution">
                                <div className="kpi-card kpi-warning">
                                    <div className="kpi-icon"><BarChart3 size={28} /></div>
                                    <div className="kpi-content">
                                        <div className="kpi-label">Spread distribuzione</div>
                                        <div className="kpi-value">{formatCurrency(data.totals.spreadMaxMin)}</div>
                                        <div className="kpi-meta">Max − Min percettori</div>
                                    </div>
                                </div>
                            </MetricTooltip>
                        )}

                        {!workerFilter && topWorkerInfo && (
                            <MetricTooltip metricKey="tipTopWorker">
                                <div className="kpi-card kpi-secondary">
                                    <div className="kpi-icon"><Users size={28} /></div>
                                    <div className="kpi-content">
                                        <div className="kpi-label">
                                            {topWorkerInfo.isUniform
                                                ? 'Distribuzione uniforme'
                                                : topWorkerInfo.isTie
                                                    ? `Top percettori (×${topWorkerInfo.topWorkers.length})`
                                                    : 'Top percettore'}
                                        </div>
                                        <div className="kpi-value" style={{ fontSize: '1.1rem', lineHeight: '1.3' }}>
                                            {topWorkerInfo.isUniform
                                                ? formatCurrency(topWorkerInfo.maxAmount) + ' ciascuno'
                                                : topWorkerInfo.topWorkers.map((w) => w.workerName).join(', ')}
                                        </div>
                                        {!topWorkerInfo.isUniform && (
                                            <div className="kpi-meta">
                                                {formatCurrency(topWorkerInfo.maxAmount)}
                                                {topWorkerInfo.isTie ? ' (pari merito)' : ''}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </MetricTooltip>
                        )}
                    </div>

                    {/* ── Reconciliation Section ────────────────────────── */}
                    {data.reconciliation && (
                        <div className="td-recon-section">
                            <div className="td-recon-header">Riconciliazione mance</div>
                            <div className="td-recon-cards">
                                <MetricTooltip metricKey="tipVenueVolume">
                                    <div className="td-recon-card">
                                        <div className="td-recon-label">GTV lordo mance</div>
                                        <div className="td-recon-value">
                                            {formatCurrency(data.reconciliation.financialBreakdown?.grossTipVolume ?? data.reconciliation.venueVolume)}
                                        </div>
                                        <div className="td-recon-sub">TipPaymentIntent succeeded</div>
                                    </div>
                                </MetricTooltip>

                                <MetricTooltip metricKey="tipDistributedTotal">
                                    <div className="td-recon-card">
                                        <div className="td-recon-label">Netto distribuito</div>
                                        <div className="td-recon-value">
                                            {formatCurrency(data.reconciliation.financialBreakdown?.netDistributed ?? data.reconciliation.totalDistributed)}
                                        </div>
                                        <div className="td-recon-sub">TipTransfer nel periodo</div>
                                    </div>
                                </MetricTooltip>

                                <MetricTooltip metricKey="tipReconciliationDiff">
                                    <div className={`td-recon-card td-recon-card--diff td-recon-card--${data.reconciliation.status}`}>
                                        <div className="td-recon-label">Differenza lorda non riconciliata</div>
                                        <div className="td-recon-value">
                                            {formatCurrency(Math.abs(data.reconciliation.difference || 0))}
                                        </div>
                                        <div className="td-recon-sub">
                                            <ReconStatusBadge status={data.reconciliation.status} />
                                        </div>
                                    </div>
                                </MetricTooltip>
                            </div>

                            <div className="td-recon-cards" style={{ marginTop: '10px' }}>
                                <div className="td-recon-card">
                                    <div className="td-recon-label">Fee Stripe</div>
                                    <div className="td-recon-value">
                                        {formatCurrency(data.reconciliation.financialBreakdown?.feeStripe || 0)}
                                    </div>
                                </div>
                                <div className="td-recon-card">
                                    <div className="td-recon-label">Fee Soontip</div>
                                    <div className="td-recon-value">
                                        {formatCurrency(data.reconciliation.financialBreakdown?.feeSoontip || 0)}
                                    </div>
                                </div>
                                <div className="td-recon-card">
                                    <div className="td-recon-label">Transazioni coperte</div>
                                    <div className="td-recon-value">
                                        {formatNumber(data.reconciliation.coverage?.coveredTransactions || 0)} / {formatNumber(data.reconciliation.coverage?.successfulTransactions || 0)}
                                    </div>
                                    <div className="td-recon-sub">
                                        {formatPct(data.reconciliation.coverage?.coveredTransactionsPercent || 0)}
                                    </div>
                                </div>
                            </div>

                            {data.reconciliation.status !== 'ok' && (
                                <div className="td-warning td-warning--recon">
                                    <AlertTriangle size={14} />
                                    La riconciliazione lorda richiede copertura completa dei TipTransfer e dei campi amountGross/amountNet/fee.
                                    In presenza di record legacy incompleti o transazioni senza transfer, la differenza può risultare parziale.
                                </div>
                            )}
                        </div>
                    )}

                    {/* ── Backend warnings ──────────────────────────────── */}
                    {data.warnings && data.warnings.length > 0 && (
                        <div className="td-warnings">
                            {data.warnings.map((w, i) => (
                                <div key={i} className={`td-warning td-warning--${w.type}`}>
                                    <AlertTriangle size={14} />
                                    {w.message}
                                </div>
                            ))}
                        </div>
                    )}

                    {/* ── Partial period warning ────────────────────────── */}
                    {hasPartialPeriods && (
                        <div className="td-warning td-warning--partial">
                            <AlertTriangle size={14} />
                            Uno o più periodi coprono solo una parte del {(() => {
                                const labels = { month: 'mese', quarter: 'trimestre', semester: 'semestre', year: 'anno' };
                                return labels[data.period.groupBy] || 'periodo';
                            })()} selezionato (visualizzati come "parziale"). I dati sono riferiti esclusivamente al range selezionato.
                        </div>
                    )}

                    {/* ── Caveat ────────────────────────────────────────── */}
                    <div className="td-caveat">
                        <strong>Importo distribuito:</strong> somma delle quote di mancia assegnate
                        al percettore tramite TipTransfer nel periodo selezionato. Non indica
                        necessariamente che il payout bancario sia già stato liquidato.
                    </div>

                    {/* ── View Toggle + Export ──────────────────────────── */}
                    <div className="td-toolbar">
                        <div className="td-view-toggle">
                            <button
                                className={`td-view-btn${viewMode === 'table' ? ' td-view-btn--active' : ''}`}
                                onClick={() => setViewMode('table')}
                            >
                                <Table size={14} />
                                Tabella
                            </button>
                            <button
                                className={`td-view-btn${viewMode === 'matrix' ? ' td-view-btn--active' : ''}`}
                                onClick={() => setViewMode('matrix')}
                            >
                                <BarChart3 size={14} />
                                Matrice
                            </button>
                        </div>
                        <button
                            className="td-export-btn"
                            onClick={handleExportCsv}
                            title="Esporta dati visibili in CSV"
                        >
                            <Download size={14} />
                            Esporta CSV
                        </button>
                    </div>

                    {/* ── Flat Table View ────────────────────────────────── */}
                    {viewMode === 'table' && (
                        <div className="analytics-table-section">
                            <table className="analytics-table td-flat-table">
                                <thead>
                                    <tr>
                                        <th>Periodo</th>
                                        <th>Percettore</th>
                                        <th className="text-right">Importo distribuito</th>
                                        <th className="text-right">% totale</th>
                                        <th className="text-right">Transazioni</th>
                                        <th className="text-right">Media / tx</th>
                                        <th>Stripe Connect</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {visibleRows.map((row, i) => {
                                        const partial = partialPeriods.has(row.periodKey);
                                        return (
                                            <tr
                                                key={i}
                                                className={row.stripeWarning ? 'td-row--stripe-warn' : ''}
                                            >
                                                <td className="td-period-cell">
                                                    {formatPeriodLabel(row.periodKey, data.period.groupBy)}
                                                    {partial && <span className="td-partial-badge">parziale</span>}
                                                </td>
                                                <td className="td-worker-cell">{row.workerName}</td>
                                                <td className="text-right td-amount-cell">
                                                    {formatCurrency(row.distributedAmount)}
                                                </td>
                                                <td className="text-right">{formatPct(row.sharePercent)}</td>
                                                <td className="text-right">{formatNumber(row.transactionCount)}</td>
                                                <td className="text-right">{formatCurrency(row.averagePerTransaction)}</td>
                                                <td>
                                                    <StripeBadge canReceive={row.canReceivePayments} />
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                                <tfoot>
                                    <tr className="td-totals-row">
                                        <td colSpan={2}><strong>Totale</strong></td>
                                        <td className="text-right"><strong>{formatCurrency(tableTotalAmount)}</strong></td>
                                        <td className="text-right"><strong>100%</strong></td>
                                        <td className="text-right"><strong>{formatNumber(tableTotalTx)}</strong></td>
                                        <td className="text-right"><strong>{formatCurrency(tableTotalAvgPerTx)}</strong></td>
                                        <td />
                                    </tr>
                                </tfoot>
                            </table>

                            {workerFilter && (
                                <p className="td-filter-note">
                                    Filtro attivo: <strong>{availableWorkers.find((w) => w.destination === workerFilter)?.workerName}</strong>.{' '}
                                    <button className="td-clear-filter" onClick={() => setWorkerFilter('')}>
                                        Rimuovi filtro
                                    </button>
                                </p>
                            )}
                        </div>
                    )}

                    {/* ── Matrix View ────────────────────────────────────── */}
                    {viewMode === 'matrix' && (
                        <div className="analytics-table-section td-matrix-section">
                            <div className="td-matrix-scroll">
                                <table className="analytics-table td-matrix-table">
                                    <thead>
                                        <tr>
                                            <th className="td-matrix-worker-col">Percettore</th>
                                            {data.matrix.periods.map((pk) => {
                                                const partial = partialPeriods.has(pk);
                                                return (
                                                    <th key={pk} className="td-matrix-period-col text-right">
                                                        {formatPeriodLabel(pk, data.period.groupBy)}
                                                        {partial && <span className="td-partial-badge">parz.</span>}
                                                    </th>
                                                );
                                            })}
                                            <th className="td-matrix-total-col text-right">Totale</th>
                                            <th>Stripe</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.matrix.workers
                                            .filter((w) => !workerFilter || w.destination === workerFilter)
                                            .map((worker, i) => {
                                                const workerTotal = Object.values(worker.periods).reduce(
                                                    (s, p) => s + p.amount,
                                                    0
                                                );
                                                return (
                                                    <tr key={i} className={!worker.canReceivePayments ? 'td-row--stripe-warn' : ''}>
                                                        <td className="td-worker-cell">{worker.workerName}</td>
                                                        {data.matrix.periods.map((pk) => {
                                                            const cell = worker.periods[pk];
                                                            return (
                                                                <td key={pk} className="text-right td-matrix-cell">
                                                                    {cell ? formatCurrency(cell.amount) : <span className="td-matrix-empty">—</span>}
                                                                </td>
                                                            );
                                                        })}
                                                        <td className="text-right td-matrix-total">
                                                            <strong>{formatCurrency(workerTotal)}</strong>
                                                        </td>
                                                        <td>
                                                            <StripeBadge canReceive={worker.canReceivePayments} />
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                    </tbody>
                                    <tfoot>
                                        <tr className="td-matrix-footer">
                                            <td><strong>Totale periodo</strong></td>
                                            {data.matrix.periods.map((pk) => {
                                                const periodTotal = data.matrix.workers
                                                    .filter((w) => !workerFilter || w.destination === workerFilter)
                                                    .reduce((s, w) => s + (w.periods[pk]?.amount || 0), 0);
                                                return (
                                                    <td key={pk} className="text-right td-matrix-total">
                                                        <strong>{formatCurrency(periodTotal)}</strong>
                                                    </td>
                                                );
                                            })}
                                            <td className="text-right td-matrix-total">
                                                <strong>
                                                    {formatCurrency(
                                                        data.matrix.workers
                                                            .filter((w) => !workerFilter || w.destination === workerFilter)
                                                            .reduce(
                                                                (s, w) =>
                                                                    s + Object.values(w.periods).reduce((ps, p) => ps + p.amount, 0),
                                                                0
                                                            )
                                                    )}
                                                </strong>
                                            </td>
                                            <td />
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </div>
                    )}
                </>
            )}
        </AnalyticsLayout>
    );
}
